import { useTranslation } from 'react-i18next'
import classNames from 'classnames/bind'
import { Alpha3Code } from 'i18n-iso-countries'

import { useCustomAccordion } from '../../components/customAccordion/useCustomAccordion'
import { OuterCard } from '../../components/outerCard/OuterCard'
import { CustomAccordion } from '../../components/customAccordion/CustomAccordion'
import generalStyles from '../../CandidateDetailsContainer.module.scss'
import { InlineAccordionAnswer } from '../../components/inlineAccordionAnswer/InlineAccordionAnswer'
import { calculateWeightedTotalMaxScore, formatScoreValue } from '../../../utils/utils'
import { createMoneyAnswers } from '../../candidateTasks/candidateReview/money/moneyAnswers'

import { formatMoney } from '@percent/utility'
import {
  AWARD_QUESTION_TYPES,
  CandidateFormSubmissionAnswer,
  DATE_QUESTION_TYPES,
  QuestionType
} from '@percent/partner-dashboard/services/grants/grantsService.types'
import { useCountries } from '@percent/partner-dashboard/common/hooks/useCountries/useCountries'
import { Accordion, Flex } from '@percent/lemonade'
import { CandidateReviewProps } from './CandidateReview.types'
import styles from './CandidateReview.module.scss'

const cx = classNames.bind(styles)

const GOODSTACK_EMAIL_DOMAIN = 'goodstack.io'
const PBP_EMAIL_DOMAIN = 'poweredbypercent.com'

export function CandidateReview({ review }: Readonly<CandidateReviewProps>) {
  const { t } = useTranslation()
  const { getCountryNameByCode } = useCountries()

  const inlineAccordionAnswers: QuestionType[] = ['score', 'decision', ...DATE_QUESTION_TYPES]

  const moneyAnswers = createMoneyAnswers(
    review.formAnswers
      .filter(answer => answer.type === 'money')
      .map(answer => ({ key: answer.questionKey, answer: answer.answer }))
  )

  const visibleAnswers = review.formAnswers.filter(
    answer => !(answer.type === 'money' && moneyAnswers.isPairedCurrency(answer.questionKey))
  )

  const itemsIds = visibleAnswers
    .filter(answer => !inlineAccordionAnswers.includes(answer.type))
    .map(answer => answer.questionId)

  const { toggleAll, handleAccordionChange, getAccordionValue, label } = useCustomAccordion(itemsIds)

  const isGoodstackReviewer = [GOODSTACK_EMAIL_DOMAIN, PBP_EMAIL_DOMAIN].some(domain =>
    review.author?.email.includes(domain)
  )

  const author = isGoodstackReviewer ? t('typography.goodstack') : review.author?.email ?? t('typography.notAvailable')

  const formatAnswer = ({ type: questionType, questionKey, answer }: CandidateFormSubmissionAnswer) => {
    if (!answer) return t('grants.reviews.noAnswer')

    if (questionType === 'money') return moneyAnswers.format(questionKey, answer)

    if (AWARD_QUESTION_TYPES.includes(questionType)) {
      return formatMoney({
        amount: Number(answer),
        currency: 'USD'
      })
    }

    if (questionType === 'country_search') {
      return getCountryNameByCode(answer as Alpha3Code) || answer
    }

    return answer
  }

  return (
    <OuterCard
      dataTestId={`review-${review.displayName}`}
      expandAll={{ label, onClick: toggleAll }}
      badgeContent={
        <div>
          {t('grants.reviews.badge.reviewBy', {
            type: review.displayName,
            author
          })}
        </div>
      }
      badgeVariant="default"
    >
      <div className={cx(generalStyles.innerCard, generalStyles.defaultBorder)} data-testid="inner-card">
        {visibleAnswers.map(question => {
          if (inlineAccordionAnswers.includes(question.type)) {
            const sharedProps = {
              key: question.questionId,
              label: question.questionLabel,
              answer: question.answer
            }

            return question.type === 'score' ? (
              <InlineAccordionAnswer
                {...sharedProps}
                type={question.type}
                score={{ maxScore: question.maxScore, scoreWeight: question.scoreWeight }}
              />
            ) : (
              <InlineAccordionAnswer {...sharedProps} type={question.type} decision={question.decision} />
            )
          }

          return (
            <div
              key={question.questionId}
              className={cx(generalStyles.innerCardSectionAccordion, generalStyles.defaultTopBorder)}
            >
              <CustomAccordion
                multiple
                value={getAccordionValue(question.questionId)}
                onChange={handleAccordionChange(question.questionId)}
              >
                <Accordion.Item value={question.questionId}>
                  <Accordion.Control>{question.questionLabel}</Accordion.Control>
                  <Accordion.Panel>{formatAnswer(question)}</Accordion.Panel>
                </Accordion.Item>
              </CustomAccordion>
            </div>
          )
        })}
      </div>
      {review.scoreAggregate && (
        <Flex justify="end" pt={6} pe={6} data-testid="review-overall-score">
          {t('grants.reviews.overallScore')}: {formatScoreValue(review.scoreAggregate.sum)} /{' '}
          {formatScoreValue(calculateWeightedTotalMaxScore(review.formAnswers))}
        </Flex>
      )}
    </OuterCard>
  )
}
